/**
 * Textos legales para el formulario de registro de viajeros
 * Internacionalizado para CA, ES, EN, FR
 * 
 * Referencias legales:
 * - Real Decreto 933/2021 (Registro de viajeros)
 * - Reglamento (UE) 2016/679 (RGPD)
 * - Ley Orgánica 3/2018 (LOPDGDD)
 */

export interface LegalTexts {
  rgpdTitle: string;
  rgpdText: string;
  mossosTitle: string;
  mossosText: string;
  signatureDisclaimer: string;
  dataRetention: string;
  rights: string;
  contact: string;
}

export const legalTexts: Record<string, LegalTexts> = {
  ca: {
    rgpdTitle: "Protecció de Dades Personals (RGPD)",
    rgpdText: `De conformitat amb el Reglament (UE) 2016/679 (RGPD) i la Llei Orgànica 3/2018 (LOPDGDD), l'informem que les seves dades personals seran tractades per HOSTAL GUILLEUMES amb les següents finalitats:

• Gestió de la reserva i estada a l'establiment
• Compliment de les obligacions legals de registre de viatgers
• Comunicació relacionada amb la seva estada
• Facturació i gestió administrativa

La base legal del tractament és l'execució del contracte d'allotjament i el compliment d'obligacions legals.`,
    
    mossosTitle: "Registre de Viatgers (RD 933/2021)",
    mossosText: `D'acord amb el Real Decret 933/2021, els establiments d'allotjament turístic estan obligats a comunicar a les autoritats competents (Mossos d'Esquadra) les dades dels viatgers allotjats en un termini màxim de 24 hores des de l'arribada.

Les dades obligatòries inclouen: nom, cognoms, document d'identitat, nacionalitat, data de naixement, sexe i adreça de residència.

Per a menors de 18 anys, és obligatori indicar la relació de parentesc amb l'adult responsable.`,
    
    signatureDisclaimer: "Amb la meva signatura, confirmo que les dades proporcionades són correctes i veraços, i accepto les condicions de la reserva i la política de privacitat.",
    
    dataRetention: "Les seves dades es conservaran durant el temps necessari per complir amb les obligacions legals (mínim 3 anys segons RD 933/2021).",
    
    rights: "Pot exercir els seus drets d'accés, rectificació, supressió, limitació, portabilitat i oposició enviant un correu a: info@guilleumes.com",
    
    contact: "Responsable del tractament: HOSTAL GUILLEUMES | Contacte: info@guilleumes.com"
  },
  
  es: {
    rgpdTitle: "Protección de Datos Personales (RGPD)",
    rgpdText: `De conformidad con el Reglamento (UE) 2016/679 (RGPD) y la Ley Orgánica 3/2018 (LOPDGDD), le informamos que sus datos personales serán tratados por HOSTAL GUILLEUMES con las siguientes finalidades:

• Gestión de la reserva y estancia en el establecimiento
• Cumplimiento de las obligaciones legales de registro de viajeros
• Comunicación relacionada con su estancia
• Facturación y gestión administrativa

La base legal del tratamiento es la ejecución del contrato de alojamiento y el cumplimiento de obligaciones legales.`,
    
    mossosTitle: "Registro de Viajeros (RD 933/2021)",
    mossosText: `De acuerdo con el Real Decreto 933/2021, los establecimientos de alojamiento turístico están obligados a comunicar a las autoridades competentes (Mossos d'Esquadra) los datos de los viajeros alojados en un plazo máximo de 24 horas desde la llegada.

Los datos obligatorios incluyen: nombre, apellidos, documento de identidad, nacionalidad, fecha de nacimiento, sexo y dirección de residencia.

Para menores de 18 años, es obligatorio indicar la relación de parentesco con el adulto responsable.`,
    
    signatureDisclaimer: "Con mi firma, confirmo que los datos proporcionados son correctos y veraces, y acepto las condiciones de la reserva y la política de privacidad.",
    
    dataRetention: "Sus datos se conservarán durante el tiempo necesario para cumplir con las obligaciones legales (mínimo 3 años según RD 933/2021).",
    
    rights: "Puede ejercer sus derechos de acceso, rectificación, supresión, limitación, portabilidad y oposición enviando un correo a: info@guilleumes.com",
    
    contact: "Responsable del tratamiento: HOSTAL GUILLEUMES | Contacto: info@guilleumes.com"
  },
  
  en: {
    rgpdTitle: "Personal Data Protection (GDPR)",
    rgpdText: `In accordance with Regulation (EU) 2016/679 (GDPR) and Spanish Organic Law 3/2018 (LOPDGDD), we inform you that your personal data will be processed by HOSTAL GUILLEUMES for the following purposes:

• Management of your reservation and stay at the establishment
• Compliance with legal obligations for traveler registration
• Communication related to your stay
• Billing and administrative management

The legal basis for processing is the execution of the accommodation contract and compliance with legal obligations.`,
    
    mossosTitle: "Traveler Registration (RD 933/2021)",
    mossosText: `In accordance with Royal Decree 933/2021, tourist accommodation establishments are required to report guest data to the competent authorities (Mossos d'Esquadra) within a maximum of 24 hours from arrival.

Mandatory data includes: name, surnames, identity document, nationality, date of birth, sex, and residential address.

For minors under 18 years of age, it is mandatory to indicate the relationship with the responsible adult.`,
    
    signatureDisclaimer: "With my signature, I confirm that the data provided is correct and truthful, and I accept the reservation conditions and privacy policy.",
    
    dataRetention: "Your data will be retained for the time necessary to comply with legal obligations (minimum 3 years according to RD 933/2021).",
    
    rights: "You can exercise your rights of access, rectification, erasure, restriction, portability, and objection by sending an email to: info@guilleumes.com",
    
    contact: "Data Controller: HOSTAL GUILLEUMES | Contact: info@guilleumes.com"
  },
  
  fr: {
    rgpdTitle: "Protection des Données Personnelles (RGPD)",
    rgpdText: `Conformément au Règlement (UE) 2016/679 (RGPD) et à la Loi Organique espagnole 3/2018 (LOPDGDD), nous vous informons que vos données personnelles seront traitées par HOSTAL GUILLEUMES aux fins suivantes:

• Gestion de votre réservation et séjour dans l'établissement
• Respect des obligations légales d'enregistrement des voyageurs
• Communication liée à votre séjour
• Facturation et gestion administrative

La base légale du traitement est l'exécution du contrat d'hébergement et le respect des obligations légales.`,
    
    mossosTitle: "Enregistrement des Voyageurs (RD 933/2021)",
    mossosText: `Conformément au Décret Royal 933/2021, les établissements d'hébergement touristique sont tenus de communiquer aux autorités compétentes (Mossos d'Esquadra) les données des voyageurs hébergés dans un délai maximum de 24 heures après l'arrivée.

Les données obligatoires comprennent: nom, prénoms, document d'identité, nationalité, date de naissance, sexe et adresse de résidence.

Pour les mineurs de moins de 18 ans, il est obligatoire d'indiquer le lien de parenté avec l'adulte responsable.`,
    
    signatureDisclaimer: "Par ma signature, je confirme que les données fournies sont correctes et véridiques, et j'accepte les conditions de réservation et la politique de confidentialité.",
    
    dataRetention: "Vos données seront conservées pendant le temps nécessaire pour respecter les obligations légales (minimum 3 ans selon RD 933/2021).",
    
    rights: "Vous pouvez exercer vos droits d'accès, de rectification, d'effacement, de limitation, de portabilité et d'opposition en envoyant un email à: info@guilleumes.com",
    
    contact: "Responsable du traitement: HOSTAL GUILLEUMES | Contact: info@guilleumes.com"
  }
};

/**
 * Obtener textos legales por idioma
 * @param lang Código de idioma (ca, es, en, fr)
 * @returns Textos legales en el idioma especificado
 */
export function getLegalTexts(lang: string): LegalTexts {
  return legalTexts[lang] || legalTexts['es'];
}

/**
 * Texto completo de política de privacidad para mostrar en modal o página
 */
export function getFullPrivacyPolicy(lang: string): string {
  const texts = getLegalTexts(lang);
  return `
${texts.rgpdTitle}
${'-'.repeat(texts.rgpdTitle.length)}

${texts.rgpdText}

${texts.mossosTitle}
${'-'.repeat(texts.mossosTitle.length)}

${texts.mossosText}

${texts.dataRetention}

${texts.rights}

${texts.contact}
  `.trim();
}
